const REPORTING_TIME_ZONE = 'Europe/Sofia';
const WEEKDAY_LABELS = ['нед', 'пон', 'вто', 'сря', 'чет', 'пет', 'съб'];
const MONTH_LABELS = [
  'яну',
  'фев',
  'мар',
  'апр',
  'май',
  'юни',
  'юли',
  'авг',
  'сеп',
  'окт',
  'ное',
  'дек'
];

const pad = (value) => String(value).padStart(2, '0');

const toDateOnly = (date) =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

const parseDateOnly = (value) => {
  const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return toDateOnly(date) === value ? date : null;
};

const addDays = (date, amount) => {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + amount);
  return result;
};

export const getToday = (now = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: REPORTING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).formatToParts(now);
  const part = (type) => parts.find((item) => item.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
};

export const getPresetRange = (period, now = new Date()) => {
  const today = parseDateOnly(getToday(now));
  const year = today.getUTCFullYear();
  const month = today.getUTCMonth();

  if (period === 'day') {
    return { from: toDateOnly(today), to: toDateOnly(today) };
  }
  if (period === 'week') {
    const weekday = today.getUTCDay();
    const from = addDays(today, -(weekday === 0 ? 6 : weekday - 1));
    return { from: toDateOnly(from), to: toDateOnly(addDays(from, 6)) };
  }
  if (period === 'year') {
    return { from: `${year}-01-01`, to: `${year}-12-31` };
  }

  return {
    from: `${year}-${pad(month + 1)}-01`,
    to: toDateOnly(new Date(Date.UTC(year, month + 1, 0)))
  };
};

export const formatEuro = (value, maximumFractionDigits = 2) =>
  new Intl.NumberFormat('bg-BG', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: Math.min(2, maximumFractionDigits),
    maximumFractionDigits
  }).format(Number(value) || 0);

export const formatPeriodLabel = (period, groupBy, long = false) => {
  const normalized = groupBy === 'year' ? `${period}-01-01` : `${period}-01`;
  const date = parseDateOnly(groupBy === 'day' || groupBy === 'week' ? period : normalized);
  if (!date) return period;

  if (groupBy === 'year') return String(date.getUTCFullYear());
  if (groupBy === 'month') {
    return new Intl.DateTimeFormat('bg-BG', {
      month: long ? 'long' : 'short',
      year: 'numeric',
      timeZone: 'UTC'
    }).format(date);
  }

  const formatted = new Intl.DateTimeFormat('bg-BG', {
    day: 'numeric',
    month: long ? 'long' : 'short',
    ...(long ? { year: 'numeric' } : {}),
    timeZone: 'UTC'
  }).format(date);
  return groupBy === 'week' ? `Седмица от ${formatted}` : formatted;
};

export const formatChartPeriodLabel = (period, groupBy) => {
  if (groupBy === 'month') {
    const match = String(period || '').match(/^(\d{4})-(\d{2})$/);
    const monthIndex = match ? Number(match[2]) - 1 : -1;
    return match && MONTH_LABELS[monthIndex]
      ? `${MONTH_LABELS[monthIndex]} ${match[1].slice(-2)}`
      : period;
  }

  const dateLabel = formatPeriodLabel(period, groupBy);
  if (groupBy !== 'day') return dateLabel;

  const date = parseDateOnly(period);
  return date ? [dateLabel, WEEKDAY_LABELS[date.getUTCDay()]] : dateLabel;
};

export const isFuturePeriod = (period, groupBy, now = new Date()) => {
  const today = getToday(now);
  if (groupBy === 'year') return period > today.slice(0, 4);
  if (groupBy === 'month') return period > today.slice(0, 7);
  if (groupBy === 'week') {
    const currentWeek = getPresetRange('week', now).from;
    return period > currentWeek;
  }
  return period > today;
};

export const isValidRange = ({ from, to }) => {
  const fromDate = parseDateOnly(from);
  const toDate = parseDateOnly(to);
  return Boolean(fromDate && toDate && fromDate <= toDate);
};
